import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";

const Table = ({
  data,
  columns,
  onClickRow,
  allowMainRowClick = false,
  disableRowHover = false,
}) => {
  const [expanded, setExpanded] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    getSubRows: (row: any) => row.subRows,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const childrenRowClickable = (row) =>
    onClickRow && (row.depth === 1 || (allowMainRowClick && row.depth === 0));

  const shouldIgnoreRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;

    return Boolean(
      target.closest(
        "button, a, input, select, textarea, label, summary, [role='button'], [data-stop-row-click='true']",
      ),
    );
  };

  const onClickRowHandler = (row, event: React.MouseEvent<HTMLTableRowElement>) => {
    if (shouldIgnoreRowClick(event)) return;

    if (row.getCanExpand() && row.depth === 0) return row.toggleExpanded();

    if (childrenRowClickable(row)) return onClickRow(row.original.id);
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <table className="w-full overflow-hidden rounded-2xl border-t bg-primary100 focus:outline-none">
          <thead className="border-b border-gray-100 bg-primary500">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`px-6 py-4 text-sm font-semibold text-gray-100 ${
                      index === 0 || index === 1 ? "text-left" : "text-center"
                    }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <React.Fragment key={row.id}>
                  <tr
                    key={row.id}
                    className={`${
                      row.id.includes(".") ? "bg-white" : "border-y border-grey200 bg-white"
                    } ${
                      disableRowHover
                        ? ""
                        : row.id.includes(".")
                          ? "hover:bg-secondary-50"
                          : "hover:bg-grey100"
                    } cursor-pointer focus:outline-none`}
                    style={{ outline: "none" }}
                    {...{ onClick: (event) => onClickRowHandler(row, event) }}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={`overflow-hidden p-6 text-sm font-semibold ${
                          index === 1
                            ? "border-grey200 text-left text-primary500"
                            : index === 0
                              ? "text-left text-primary500"
                              : "text-center text-primary300"
                        } ${
                          row.id.includes(".")
                            ? `${index === 0 && "border-l-4 border-primary300 pl-12"} `
                            : ""
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Table;
